/**
 * Tema pietei, calculata din config si aplicata ca variabile CSS.
 *
 * Doua reguli, ambele cu consecinte vizibile daca sunt incalcate:
 *
 *  1. NIMIC nu se injecteaza in CSS fara validare. Configul vine din Hub si
 *     ajunge nefiltrat intr-un `style`; un `primaryColor` de forma
 *     `red; background: url(...)` ar fi o injectare CSS. Ce nu trece de
 *     validare e ignorat si se cade pe o valoare derivata, nu pe textul brut.
 *
 *  2. Culorile derivate se CALCULEAZA, nu se ghicesc. Configul da o culoare de
 *     brand si, optional, fundalul; restul (text estompat, chenar, suprafata,
 *     culoarea textului pe butonul de brand) se obtine prin amestec si se
 *     verifica pe contrast. Altfel un brand galben produce un buton alb pe
 *     galben, ilizibil, si nimeni nu vede asta pana nu ajunge o piata cu galben.
 *
 * Valorile neutre de mai jos nu sunt branding: sunt ce se foloseste EXCLUSIV
 * cand configul omite un camp optional. Culoarea principala e obligatorie in
 * contract si nu are inlocuitor aici.
 */

import type { PublicMarketConfig } from "@deliveryapp/shared";

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Fundal si text pentru cazul in care configul nu le declara. */
const NEUTRAL_LIGHT = {
  bg: { r: 255, g: 255, b: 255 },
  text: { r: 20, g: 22, b: 26 },
} as const;
const NEUTRAL_DARK = {
  bg: { r: 16, g: 17, b: 20 },
  text: { r: 244, g: 245, b: 247 },
} as const;

/** Semnalele de stare nu sunt brand: rosu/verde/chihlimbar au inteles fix. */
const SIGNAL_DANGER = {
  light: { r: 180, g: 35, b: 24 },
  dark: { r: 249, g: 112, b: 102 },
} as const;
const SIGNAL_SUCCESS = {
  light: { r: 6, g: 118, b: 71 },
  dark: { r: 71, g: 205, b: 137 },
} as const;
const SIGNAL_WARNING = {
  light: { r: 181, g: 71, b: 8 },
  dark: { r: 253, g: 176, b: 34 },
} as const;

const SYSTEM_FONT_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const RADIUS_BY_KIND: Record<NonNullable<PublicMarketConfig["theme"]["borderRadius"]>, { card: string; control: string }> = {
  sharp: { card: "0px", control: "0px" },
  rounded: { card: "0.875rem", control: "0.625rem" },
  pill: { card: "1.5rem", control: "9999px" },
};

export type ColorScheme = "light" | "dark";

// ---------------------------------------------------------------------------
// Culoare: parsare, contrast, amestec
// ---------------------------------------------------------------------------

/** Accepta doar `#rgb` si `#rrggbb`. Orice altceva e refuzat, nu "curatat". */
export function parseHexColor(value: string | null | undefined): Rgb | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(text);
  if (short) {
    const digits = short[1] ?? "";
    const r = digits.charAt(0);
    const g = digits.charAt(1);
    const b = digits.charAt(2);
    return {
      r: Number.parseInt(`${r}${r}`, 16),
      g: Number.parseInt(`${g}${g}`, 16),
      b: Number.parseInt(`${b}${b}`, 16),
    };
  }
  const long = /^#([0-9a-fA-F]{6})$/.exec(text);
  if (!long) return null;
  const digits = long[1] ?? "";
  return {
    r: Number.parseInt(digits.slice(0, 2), 16),
    g: Number.parseInt(digits.slice(2, 4), 16),
    b: Number.parseInt(digits.slice(4, 6), 16),
  };
}

export function toHex(color: Rgb): string {
  const channel = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0");
  return `#${channel(color.r)}${channel(color.g)}${channel(color.b)}`;
}

/** Luminanta relativa WCAG 2.1. */
export function relativeLuminance(color: Rgb): number {
  const channel = (raw: number): number => {
    const value = Math.max(0, Math.min(255, raw)) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Amesteca `from` spre `to`; `ratio` e cat din `to` intra in rezultat. */
export function mixColors(from: Rgb, to: Rgb, ratio: number): Rgb {
  const t = Math.max(0, Math.min(1, ratio));
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  };
}

/** Culoarea de text lizibila peste un fundal dat: cea cu contrastul mai mare. */
export function readableOn(background: Rgb): Rgb {
  const light: Rgb = { r: 255, g: 255, b: 255 };
  const dark: Rgb = { r: 20, g: 22, b: 26 };
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}

/**
 * Impinge `foreground` spre alb sau spre negru pana atinge contrastul cerut fata
 * de `background`. Daca nici extrema nu il atinge (fundal cenusiu la mijloc),
 * intoarce extrema cea mai buna: mai bine cel mai lizibil posibil decat o
 * culoare aleasa pentru ca "arata bine".
 */
export function ensureContrast(foreground: Rgb, background: Rgb, minimum: number): Rgb {
  if (contrastRatio(foreground, background) >= minimum) return foreground;
  const target: Rgb = relativeLuminance(background) > 0.5 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
  let best = foreground;
  let bestRatio = contrastRatio(foreground, background);
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixColors(foreground, target, step / 20);
    const ratio = contrastRatio(candidate, background);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
    if (ratio >= minimum) return candidate;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Tipografie
// ---------------------------------------------------------------------------

/**
 * Familia de fonturi e text liber in config si ajunge intr-o declaratie CSS.
 * Se accepta doar ce poate aparea intr-o lista de fonturi: litere, cifre,
 * spatii, virgule, cratime, ghilimele. `;`, `(`, `)`, `{`, `}` sunt refuzate —
 * cu ele s-ar putea inchide declaratia si deschide alta.
 */
export function sanitizeFontFamily(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (text.length === 0 || text.length > 200) return null;
  if (!/^[A-Za-z0-9 ,'"_-]+$/.test(text)) return null;
  return text;
}

// ---------------------------------------------------------------------------
// Construirea variabilelor
// ---------------------------------------------------------------------------

export interface ThemeVariables {
  variables: Record<string, string>;
  scheme: ColorScheme;
  /** Campurile refuzate la validare; le foloseste ecranul de diagnostic. */
  rejected: string[];
}

export function resolveColorScheme(
  config: PublicMarketConfig,
  prefersDark: boolean,
): ColorScheme {
  const declared = config.theme.colorScheme ?? "auto";
  if (declared === "light" || declared === "dark") return declared;
  return prefersDark ? "dark" : "light";
}

export function buildThemeVariables(config: PublicMarketConfig, scheme: ColorScheme): ThemeVariables {
  const rejected: string[] = [];

  const read = (field: string, raw: string | undefined): Rgb | null => {
    if (raw === undefined || raw === null || raw === "") return null;
    const parsed = parseHexColor(raw);
    if (!parsed) rejected.push(field);
    return parsed;
  };

  const neutral = scheme === "dark" ? NEUTRAL_DARK : NEUTRAL_LIGHT;
  const background = read("theme.backgroundColor", config.theme.backgroundColor) ?? neutral.bg;
  const declaredText = read("theme.textColor", config.theme.textColor);
  const text = ensureContrast(declaredText ?? neutral.text, background, 7);

  // Culoarea principala e obligatorie in contract. Daca totusi vine invalida,
  // nu inventam un brand: folosim culoarea textului, care e garantat lizibila.
  // Piata arata monocrom si greseala se vede imediat, in loc sa fie mascata.
  const primary = read("theme.primaryColor", config.theme.primaryColor) ?? text;
  const secondary = read("theme.secondaryColor", config.theme.secondaryColor) ?? primary;
  const accent = read("theme.accentColor", config.theme.accentColor) ?? primary;

  const primaryOnSurface = ensureContrast(primary, background, 4.5);
  const primaryContrast = readableOn(primary);
  const primarySoft = mixColors(background, primary, scheme === "dark" ? 0.22 : 0.12);
  const surface = mixColors(background, text, scheme === "dark" ? 0.06 : 0.03);
  const surfaceRaised = mixColors(background, text, scheme === "dark" ? 0.11 : 0.06);
  const border = mixColors(background, text, scheme === "dark" ? 0.22 : 0.16);
  const textMuted = ensureContrast(mixColors(background, text, 0.62), background, 4.5);

  const danger = scheme === "dark" ? SIGNAL_DANGER.dark : SIGNAL_DANGER.light;
  const success = scheme === "dark" ? SIGNAL_SUCCESS.dark : SIGNAL_SUCCESS.light;
  const warning = scheme === "dark" ? SIGNAL_WARNING.dark : SIGNAL_WARNING.light;

  // Tipul spune ca `borderRadius` e una din trei valori, dar valoarea vine din
  // JSON-ul publicat in Hub: la runtime poate fi orice. Cautarea se face pe
  // cheie de tip string tocmai ca sa existe ramura de refuz.
  const radiusKind = config.theme.borderRadius ?? "rounded";
  const radiusTable: Record<string, { card: string; control: string } | undefined> = RADIUS_BY_KIND;
  const radius = radiusTable[radiusKind];
  if (radius === undefined) {
    rejected.push("theme.borderRadius");
  }
  const resolvedRadius = radius ?? RADIUS_BY_KIND.rounded;

  const font = sanitizeFontFamily(config.theme.fontFamily);
  if (config.theme.fontFamily !== undefined && config.theme.fontFamily !== "" && font === null) {
    rejected.push("theme.fontFamily");
  }

  const variables: Record<string, string> = {
    "--da-primary": toHex(primary),
    "--da-primary-on-surface": toHex(primaryOnSurface),
    "--da-primary-contrast": toHex(primaryContrast),
    "--da-primary-soft": toHex(primarySoft),
    /*
      Starile interactive, calculate AICI si emise ca hex.
      Varianta prin `color-mix()` in CSS avea un mod de esec urat: pe un browser
      care nu il cunoaste, valoarea variabilei devine invalida la substituire,
      iar proprietatea cade pe valoarea initiala — `background-color: transparent`.
      Adica exact butonul principal („+", „Vezi coșul") devenea invizibil, cu
      textul lui alb pe fundalul cardului. Amestecul se face cu culorile REALE ale
      pietei, deci pe tema inchisa starile lumineaza, nu intuneca.
    */
    "--da-primary-hover": toHex(mixColors(primary, text, 0.12)),
    "--da-surface-hover": toHex(mixColors(surface, text, 0.08)),
    "--da-secondary": toHex(ensureContrast(secondary, background, 4.5)),
    "--da-accent": toHex(ensureContrast(accent, background, 3)),
    "--da-bg": toHex(background),
    "--da-surface": toHex(surface),
    "--da-surface-raised": toHex(surfaceRaised),
    "--da-text": toHex(text),
    "--da-text-muted": toHex(textMuted),
    "--da-border": toHex(border),
    "--da-danger": toHex(ensureContrast(danger, background, 4.5)),
    "--da-danger-soft": toHex(mixColors(background, danger, scheme === "dark" ? 0.2 : 0.1)),
    "--da-success": toHex(ensureContrast(success, background, 4.5)),
    "--da-warning": toHex(ensureContrast(warning, background, 4.5)),
    "--da-radius": resolvedRadius.card,
    "--da-radius-control": resolvedRadius.control,
    "--da-font": font === null ? SYSTEM_FONT_STACK : `${font}, ${SYSTEM_FONT_STACK}`,
  };

  return { variables, scheme, rejected };
}

/**
 * Aplica tema pe `<html>` si o reaplica la schimbarea preferintei de sistem
 * cand configul cere `auto`. Intoarce functia de dezabonare.
 */
export function applyTheme(config: PublicMarketConfig, root: HTMLElement): () => void {
  const media = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

  const paint = (): void => {
    const scheme = resolveColorScheme(config, media?.matches === true);
    const { variables } = buildThemeVariables(config, scheme);
    for (const [name, value] of Object.entries(variables)) {
      root.style.setProperty(name, value);
    }
    root.style.colorScheme = scheme;
    root.dataset.colorScheme = scheme;
  };

  paint();

  if (media === null || (config.theme.colorScheme ?? "auto") !== "auto") {
    return () => undefined;
  }
  media.addEventListener("change", paint);
  return () => media.removeEventListener("change", paint);
}
